/* eslint-disable no-underscore-dangle */
import { $t } from '@/i18n';
import { useStore } from '@/store';
import { hasCompleteArtifactCos, loadArtifactCosStylesheets, preloadArtifactCosAssets, disposeArtifactCosStylesheets } from '@/utils/aiProjectInfo';
import { useGogoRemoteCssReset } from './css-reset-counter';
import { useSetupAICheck } from './check/setup';
import { readGogoRemotePreviewConnection } from './gogoRemotePreviewConnection';
import { AsyncGogoRemoteHost, loadGogoLib, loadGogoRemoteStuff, loadVue3Stuff } from './runtime-loader';
import { defineComponent, onMounted, onUnmounted, provide, ref, shallowRef } from '@vue/composition-api';
import { getWujiBaseModules } from './useGogoRuntimeEnv';
import { notifyVue3RouterChanged } from './vue3router';
import useAiDepLoader from './xy-logic/use-ai-dep-loader';
import { WUJI_DEPS_KEY } from '@utils/dependencyLoader/consts';
import { createDebug } from 'obug';
import { resolveI18nField } from './utils/i18n-field';
const debug = createDebug('wuji-gogo:xy-client:GogoRuntimeRemoteWrapper');
const GOGO_CTX_MODULES_KEY = '__GOGO_CTX_MODULES__';
export default defineComponent({
    name: 'GogoRuntimeRemoteWrapper',
    components: {
        AsyncGogoRemoteHost,
    },
    props: {
        projectId: {
            type: String,
            required: true,
        },
        env: {
            type: String,
            required: true,
        },
        branch: {
            type: String,
            required: true,
        },
    },
    setup(props) {
        useGogoRemoteCssReset();
        const { markReady } = useSetupAICheck();
        const loadLib = loadGogoRemoteStuff();
        const { loadAIPageDependency } = useAiDepLoader();
        const gogoRuntimeViewInstances = shallowRef([]);
        const routeViewContainer = shallowRef(null);
        const gogoHostReady = ref(true);
        const remoteHost = shallowRef(null);
        const src = ref('');
        const search = new URLSearchParams(window.location.search);
        const { wsUrl } = readGogoRemotePreviewConnection();
        const hasPreviewBridge = !!search.get('wujiAiPageUK');
        // iframe=1 只表示「编辑器内预览」。顶层打开同一条 URL 时没有 MessagePort 对端，
        // 必须走 wsUrl 的 devenv 桥，否则会一直停在「正在加载页面预览」。
        const isPreviewIFrame = hasPreviewBridge && search.get('iframe') === '1' && window.parent !== window;
        const useLiveWs = Boolean(wsUrl);
        const loadStatus = ref((isPreviewIFrame || useLiveWs) ? 'ready' : 'loading');
        const loadError = ref('');
        provide('gogoRuntimeViewInstances', gogoRuntimeViewInstances);
        provide('routeViewContainer', routeViewContainer);
        provide('gogoHostReady', gogoHostReady);
        provide('gogoPortalHostClass', 'wujiGogo-remote-host');
        function handleHostReady(host) {
            remoteHost.value = host;
            gogoHostReady.value = true;
            markReady(loadLib);
            syncGogoHijackFlag(host);
            watchPageDescriptors(host);
        }
        // 与 GogoRuntimeRouterWrapper 对齐：预览态(存在 __gogoDebugHijack)时，把当前是否命中 gogo 页
        // 同步给 hijack 组件，让根路径 '/' 的 hijack 占位能渲染出 AI 页面。
        // 必须用 vue3 的 watchEffect 才能追踪 remote host 的 vue3 响应式 currentMatchedRoute。
        function syncGogoHijackFlag(host) {
            const gogoHijack = window.__gogoDebugHijack;
            if (!gogoHijack)
                return;
            void loadVue3Stuff().then((stuff) => {
                const { watchEffect } = stuff.vue;
                watchEffect(() => {
                    gogoHijack.currentRouteIsGogoPage = host.context?.currentMatchedRoute?.value?.descriptor?.file || '';
                });
            });
        }
        // pageDescriptors 是 vue3 ref；HMR/新增页时会更新，需用 vue3 watch 才能追踪。
        function watchPageDescriptors(host) {
            void loadVue3Stuff().then((stuff) => {
                const { watch, watchEffect } = stuff.vue;
                watch(() => host.context?.pageDescriptors?.value, (pages) => {
                    debug('pages', pages);
                    if (Array.isArray(pages)) {
                        notifyVue3RouterChanged({ pages, useRemoteRuntime: true });
                    }
                }, { deep: true, immediate: true });
                // 追踪 currentMatchedRoute，对齐 GogoRuntimeRouterWrapper 的 i18nPatch 逻辑
                watchEffect(() => {
                    const gogoPage = host.context?.currentMatchedRoute?.value?.gogoPage;
                    const rawMeta = (gogoPage?.meta ?? {});
                    const title = resolveI18nField({ raw: rawMeta.title, patchJson: rawMeta.i18nPatch?.title });
                    const description = resolveI18nField({
                        raw: rawMeta.description,
                        patchJson: rawMeta.i18nPatch?.description,
                    });
                    const metaKeywords = resolveI18nField({
                        raw: rawMeta.metaKeywords,
                        patchJson: rawMeta.i18nPatch?.metaKeywords,
                    });
                    // 没有渲染的时候，不需要更新 title 和 description
                    if (!gogoRuntimeViewInstances.value.length)
                        return;
                    if (title) {
                        document.title = title;
                    }
                    if (description) {
                        let el = document.querySelector('meta[name="description"]');
                        if (!el) {
                            el = document.createElement('meta');
                            el.setAttribute('name', 'description');
                            document.head.appendChild(el);
                        }
                        el.setAttribute('content', description);
                    }
                    if (metaKeywords) {
                        let el = document.querySelector('meta[name="keywords"]');
                        if (!el) {
                            el = document.createElement('meta');
                            el.setAttribute('name', 'keywords');
                            document.head.appendChild(el);
                        }
                        el.setAttribute('content', metaKeywords);
                    }
                });
            });
        }
        function handlePointerDown(e) {
            remoteHost.value?.handlePointerDown?.(e);
        }
        function abortEventIfPicking(e) {
            remoteHost.value?.abortEventIfPicking?.(e);
        }
        const beforeLoadRemoteModule = async () => {
            // 编辑器 bundler 产物会 external `@wuji-gogo/runtime`（patchVueSFCExports 等）
            const gogoRuntime = await loadGogoLib('runtime');
            window[GOGO_CTX_MODULES_KEY] = {
                ...window[GOGO_CTX_MODULES_KEY] || {},
                ...getWujiBaseModules({ projectId: props.projectId }),
                ...(window[WUJI_DEPS_KEY] || {}),
                '@wuji-gogo/runtime': gogoRuntime.runtimeShared,
            };
        };
        onMounted(async () => {
            if (isPreviewIFrame || useLiveWs)
                return;
            try {
                const store = useStore();
                const aiProjectInfo = await store.dispatch('runtime/getAiProjectInfo', {
                    projectId: props.projectId,
                    env: props.env,
                    branchId: props.branch,
                });
                if (!hasCompleteArtifactCos(aiProjectInfo)) {
                    loadError.value = $t('k_dca31870');
                    loadStatus.value = 'error';
                    return;
                }
                if (store.state.runtime.project?.aiAdvanceConfig?.useRemoteEditor !== true) {
                    await loadAIPageDependency(true, 'vue3');
                }
                // 只 preload / 等待入口资源；页面 chunk 由 defineAsyncComponent 按需 import
                preloadArtifactCosAssets(aiProjectInfo.artifactCos);
                await loadArtifactCosStylesheets(aiProjectInfo.artifactCos);
                src.value = aiProjectInfo.artifactCos.js;
                loadStatus.value = 'ready';
            }
            catch (error) {
                console.error('[xy-client] load aiProjectInfo for remote runtime failed', error);
                loadError.value = $t('k_6be6212c', {
                    arg_0: error instanceof Error ? error.message : String(error),
                });
                loadStatus.value = 'error';
            }
        });
        onUnmounted(() => {
            disposeArtifactCosStylesheets();
        });
        return {
            wsUrl,
            hasPreviewBridge,
            src,
            isPreviewIFrame,
            loadStatus,
            loadError,
            routeViewContainer,
            handleHostReady,
            handlePointerDown,
            abortEventIfPicking,
            beforeLoadRemoteModule,
        };
    },
});
